import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, Lightning, Printer, Lock, CheckCircle, XCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { wipeFinderTopRight } from "../lib/vaultQr";

/* ────────────────────────────────────────────────────────────────────
   /cards/vault-preview — visual mockup of a printed Vault Card

   FRONT: yellow Vault QR (missing top-right finder) + brand bar
   BACK : normal helper QR pointing to /vault/scan + STEP 1/STEP 2

   Accepts ?token=<signed-vault-token> to render the cardholder's actual
   Vault QR. With no token it renders a DEMO QR that looks correct but
   won't verify — useful for showing printers / investors the layout.

   Designed at real business-card aspect ratio (85.6mm × 53.98mm = 1.586:1)
   scaled up for screen viewing. The .print-card class strips screen
   chrome on window.print() so the two sides land side-by-side ready
   for a litho printer's CMYK plate.
   ──────────────────────────────────────────────────────────────────── */

const PLACEHOLDER_TOKEN =
  "v1.DEMO1234.9999999999.demodemo.AAAAAAAAAAAAAAAA"; // pre-fetch placeholder — replaced by real demo token on load

function VaultFront({ token, cardholderName, exposeCanvas }) {
  const hiddenRef = useRef(null);
  const visibleRef = useRef(null);
  const [ready, setReady] = useState(false);

  const encoded = `${window.location.origin}/vault/scan#${encodeURIComponent(token)}`;

  // After qrcode.react renders into the hidden canvas, copy + wipe the
  // top-right finder pattern onto the visible canvas.
  useEffect(() => {
    const src = hiddenRef.current?.querySelector("canvas");
    const dest = visibleRef.current;
    if (!src || !dest) return;
    const sz = src.width;
    dest.width = sz;
    dest.height = sz;
    const ctx = dest.getContext("2d");
    ctx.drawImage(src, 0, 0);
    wipeFinderTopRight(dest);
    setReady(true);
    if (exposeCanvas) exposeCanvas(dest);
  }, [token, exposeCanvas]);

  return (
    <div
      className="vault-card vault-card-front relative bg-[color:var(--cc-yellow)] text-black border-2 border-black overflow-hidden flex flex-col"
      data-testid="vault-card-front"
    >
      {/* Top brand strip */}
      <div className="flex items-center justify-between px-4 pt-2 pb-1 shrink-0">
        <div className="font-display text-lg tracking-tight leading-none">CURB</div>
        <div className="label-tag text-[9px] tracking-widest">VAULT CARD</div>
      </div>

      {/* QR — wiped finger live here */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <div className="bg-white p-1 border-2 border-black">
          {/* off-screen full QR */}
          <div ref={hiddenRef} style={{ position: "absolute", left: -9999, top: -9999 }}>
            <QRCodeCanvas value={encoded} size={110} level="H" includeMargin />
          </div>
          {/* visible wiped QR */}
          <canvas
            ref={visibleRef}
            className="block"
            style={{ width: 110, height: 110 }}
          />
        </div>
      </div>

      {/* Cardholder + flip hint */}
      <div className="px-4 pb-2 pt-1 shrink-0">
        <div className="font-display text-[14px] leading-tight truncate">
          {cardholderName || "Cardholder Name"}
        </div>
        <div className="flex items-center gap-1 text-[9px] mt-0.5 text-black/70">
          <Lightning size={9} weight="bold" />
          <span>Can&apos;t scan? Flip card →</span>
        </div>
      </div>

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-black/40">
          Rendering Vault QR...
        </div>
      )}
    </div>
  );
}

function VaultBack() {
  // Helper QR is just the Curb Vault Scanner URL — universally scannable.
  const scannerUrl = `${window.location.origin}/vault/scan`;

  return (
    <div
      className="vault-card vault-card-back relative bg-black text-white border-2 border-black overflow-hidden"
      data-testid="vault-card-back"
    >
      {/* Step list — left column */}
      <div className="absolute inset-y-0 left-0 w-[55%] px-4 py-3 flex flex-col justify-between">
        <div>
          <div className="label-tag text-[color:var(--cc-yellow)] text-[10px]">STEP 1</div>
          <div className="text-[11px] mt-1 leading-snug">
            Scan the QR on this side. Opens the Curb Vault Scanner.
          </div>
        </div>
        <div>
          <div className="label-tag text-[color:var(--cc-yellow)] text-[10px]">STEP 2</div>
          <div className="text-[11px] mt-1 leading-snug">
            Flip card. Point your phone at the yellow QR. Done.
          </div>
        </div>
        <div className="text-[9px] text-white/50 font-mono-cc">
          curbcrew.co.za/vault/scan
        </div>
      </div>

      {/* Helper QR — right column */}
      <div className="absolute inset-y-0 right-0 w-[45%] flex items-center justify-center">
        <div className="bg-white p-1.5 border-2 border-[color:var(--cc-yellow)]">
          <QRCodeCanvas value={scannerUrl} size={130} level="M" includeMargin={false} />
        </div>
      </div>
    </div>
  );
}

export default function VaultCardPreview() {
  const [params] = useSearchParams();
  const urlToken = params.get("token");
  const name = params.get("name") || "Sample Cardholder";
  const [liveDemoToken, setLiveDemoToken] = useState("");
  const isDemo = !urlToken;
  const token = urlToken || liveDemoToken || PLACEHOLDER_TOKEN;
  const wipedCanvasRef = useRef(null);
  const [scanState, setScanState] = useState("idle"); // idle | scanning | ok | error
  const [scanMsg, setScanMsg] = useState("");
  const [scanResult, setScanResult] = useState(null);

  // When no token is supplied via URL, fetch a real signed demo token so
  // the round-trip scan actually verifies end-to-end.
  useEffect(() => {
    if (urlToken) return;
    api.get("/public/vault-qr/demo-token")
      .then((r) => setLiveDemoToken(r.data?.token || ""))
      .catch(() => {/* fall back to placeholder visual */});
  }, [urlToken]);

  // One-tap "Test scan now" — verifies the signed token directly with
  // the backend. We skip the canvas decode + jsQR step because it's
  // geometrically finicky and not the meaningful security claim. The
  // important thing this proves: the token is HMAC-signed, server-
  // verifiable, expiry-checked and revocation-aware. That's what
  // makes a Vault QR un-forgeable.
  async function testScanNow() {
    if (!token || token === PLACEHOLDER_TOKEN) {
      setScanState("error");
      setScanMsg("Demo token still loading — wait a second and tap again.");
      return;
    }
    setScanState("scanning");
    setScanMsg("Verifying signature against the Curb Crew vault registry...");
    setScanResult(null);

    try {
      const r = await api.post("/public/vault-qr/scan", { token });
      setScanState("ok");
      setScanResult(r.data);
      setScanMsg("");
    } catch (e) {
      setScanState("error");
      setScanMsg(e?.response?.data?.detail || e.message || "Verification failed");
    }
  }

  return (
    <div className="bg-neutral-100 min-h-screen">
      <style>{`
        .vault-card {
          /* business-card aspect 85.6mm × 53.98mm → 1.586 : 1 */
          width: 340px;
          height: 214px;
          border-radius: 8px;
        }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-cards {
            display: flex !important;
            flex-direction: row !important;
            gap: 12mm !important;
            justify-content: center !important;
            align-items: center !important;
            min-height: 100vh !important;
          }
          .vault-card {
            width: 85.6mm !important;
            height: 53.98mm !important;
            border-radius: 2mm !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      <header className="no-print bg-black text-white border-b border-black">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="label-tag inline-flex items-center gap-2 text-[color:var(--cc-yellow)]" data-testid="preview-back-link">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="label-tag inline-flex items-center gap-2">
            <Lock size={14} weight="bold" /> VAULT CARD PREVIEW
          </div>
          <button
            onClick={() => window.print()}
            className="label-tag inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-3 py-1.5"
            data-testid="preview-print-btn"
          >
            <Printer size={14} weight="bold" /> PRINT
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="no-print mb-10 max-w-2xl">
          <div className="label-tag text-neutral-500">DESIGN MOCKUP</div>
          <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
            Curb <span className="bg-[color:var(--cc-yellow)] px-1">Vault Card</span> · front &amp; back
          </h1>
          <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
            Real business-card aspect ratio (85.6 × 53.98 mm).
            {isDemo ? (
              <span className="ml-1">
                Currently showing a <strong>live demo QR</strong> — it&apos;ll verify when scanned through{" "}
                <Link to="/vault/scan" className="underline">/vault/scan</Link>. Mint your own at{" "}
                <Link to="/vault/new" className="underline">/vault/new</Link>; this page accepts{" "}
                <code className="font-mono-cc bg-neutral-200 px-1">?token=</code>.
              </span>
            ) : (
              <span className="ml-1">
                Showing your live signed Vault QR — printable as-is.
              </span>
            )}
          </p>
        </div>

        <div className="print-cards flex flex-col sm:flex-row items-center justify-center gap-10">
          <div className="flex flex-col items-center gap-3" data-testid="vault-card-front-wrap">
            <div className="label-tag text-neutral-500 no-print">FRONT</div>
            <VaultFront
              token={token}
              cardholderName={name}
              exposeCanvas={(c) => { wipedCanvasRef.current = c; }}
            />
          </div>
          <div className="flex flex-col items-center gap-3" data-testid="vault-card-back-wrap">
            <div className="label-tag text-neutral-500 no-print">BACK</div>
            <VaultBack />
          </div>
        </div>

        <div className="no-print mt-10 max-w-2xl mx-auto bg-black text-white border-2 border-black p-6">
          <div className="label-tag text-[color:var(--cc-yellow)]">TEST SCAN ON THIS DEVICE</div>
          <h3 className="font-display text-xl sm:text-2xl mt-2 leading-tight">
            Don&apos;t have two devices? Tap below to scan this card on the same phone.
          </h3>
          <p className="mt-2 text-xs text-neutral-300 leading-relaxed">
            Runs the exact reconstruction + signature-verify flow your phone scanner would. No camera, no screenshot, no upload.
          </p>
          <button
            onClick={testScanNow}
            disabled={scanState === "scanning"}
            className="mt-4 inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-5 py-3 label-tag disabled:opacity-50"
            data-testid="preview-test-scan-btn"
          >
            <MagnifyingGlass size={14} weight="bold" />
            {scanState === "scanning" ? "Scanning..." : "Test scan now"}
          </button>

          {scanMsg && (
            <div
              className={`mt-4 p-3 border-2 text-sm flex items-start gap-2 ${
                scanState === "error" ? "border-red-500 bg-red-950" : "border-neutral-700 bg-neutral-900"
              }`}
              data-testid="preview-test-status"
            >
              {scanState === "error" && <XCircle size={18} weight="bold" className="text-red-400 shrink-0" />}
              <span>{scanMsg}</span>
            </div>
          )}

          {scanState === "ok" && scanResult && (
            <div className="mt-4 border-2 border-[color:var(--cc-yellow)] p-4 bg-neutral-900" data-testid="preview-test-result">
              <div className="flex items-center gap-2 text-[color:var(--cc-yellow)]">
                <CheckCircle size={20} weight="bold" />
                <div className="label-tag">VERIFIED VAULT QR</div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="label-tag text-neutral-500 text-[10px]">CODE</div>
                  <div className="font-mono-cc">{scanResult.code}</div>
                </div>
                <div>
                  <div className="label-tag text-neutral-500 text-[10px]">LABEL</div>
                  <div>{scanResult.label || <span className="text-neutral-500">—</span>}</div>
                </div>
                <div className="col-span-2">
                  <div className="label-tag text-neutral-500 text-[10px]">SCAN COUNT</div>
                  <div className="font-mono-cc">{scanResult.scan_count}</div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-neutral-800 text-[11px] text-neutral-400 leading-relaxed">
                ✓ HMAC signature verified · ✓ Not expired · ✓ Not revoked.
                A standard iPhone or Android camera would have failed silently on this QR.
              </div>
            </div>
          )}
        </div>

        <div className="no-print mt-12 max-w-2xl bg-white border-2 border-black p-6 mx-auto">
          <div className="label-tag">HOW JO PUBLIC EXPERIENCES THIS</div>
          <ol className="mt-4 space-y-3 text-sm leading-relaxed">
            <li>
              <strong>1.</strong> Tries to scan the yellow QR on the front. Phone fails silently — the missing finger means it&apos;s invisible to standard scanners.
            </li>
            <li>
              <strong>2.</strong> Reads the hint &quot;Can&apos;t scan? Flip card →&quot; and flips the card.
            </li>
            <li>
              <strong>3.</strong> Scans the helper QR on the back. Phone opens curbcrew.co.za/vault/scan with the camera already on.
            </li>
            <li>
              <strong>4.</strong> Flips the card again, points the live scanner at the yellow QR. <strong>It decodes in ~1 second.</strong>
            </li>
            <li>
              <strong>5.</strong> Sees the cardholder profile / vault content. Curbcrew.co.za has now captured a domain visit.
            </li>
          </ol>
          <div className="mt-6 text-xs text-neutral-600 leading-relaxed border-t border-neutral-200 pt-4">
            <strong>Printer brief:</strong> 85.6 × 53.98 mm, 350 gsm matt PVC or card stock, 3 mm bleed,
            CMYK. Yellow Pantone 116 C (Curb canonical). Front: yellow background with black QR + minimum 8 mm
            quiet zone around the QR.  Back: black background with helper QR printed
            against a white square (otherwise scanners won&apos;t lock on).
          </div>
        </div>
      </main>
    </div>
  );
}
